import React from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    cn
} from 'shared/lib/utils';
import ResourcePicker from '../../components/ResourcePicker';
import {
    StepCommonAdvanced, StepCommonInlineFields, StepOelEditor,
    useProcedureStepIds
} from './_shared';
import {
    LlmStepZone, LlmLabeled, LlmStepCanvas, llmFocusInputClass
} from './llmStepLayout';

/**
 * InlineRAGStep — kind:"rag".
 * Retrieves passages from a KB collection. Schema (V2StepRAG.tsx):
 *   { operation: basic_search | search_with_files | retrieve_files,
 *     inputs: { collection_id, query, top_k?, top_n?, use_rerank?,
 *               file_keys?, page_size? } }
 * top_n is required for search_with_files (server schema).
 */
const OPERATIONS = [
    {
        value: 'basic_search',
        label: 'basic_search',
        desc: 'Top-k passages by vector similarity.'
    },
    {
        value: 'search_with_files',
        label: 'search_with_files',
        desc: 'Search restricted to a set of files.'
    },
    {
        value: 'retrieve_files',
        label: 'retrieve_files',
        desc: 'Fetch full files (no semantic search).'
    }
];

export default function InlineRAGStep({
    step, onPatchStep, oelContext
}) {
    const inputs = step.inputs || {};
    const operation = step.operation || 'basic_search';

    const patch = (p) => onPatchStep({
        ...step,
        ...p
    });
    const patchInputs = (p) => patch({
        inputs: {
            ...inputs,
            ...p
        }
    });

    // collection_id is stored as an OEL expression. A picked collection is a
    // single-quoted literal ('id'); strip the quotes so ResourcePicker (which
    // matches on the bare id) can highlight the current selection. An OEL
    // reference (@input...) is left as-is and simply won't match a chip.
    const bareCollectionId = (inputs.collection_id || '').replace(/^'(.*)'$/, '$1');

    const useRerankOn = inputs.use_rerank === true || inputs.use_rerank === 'true';
    const showRerank = operation === 'basic_search' || operation === 'search_with_files';
    const showFileKeys = operation !== 'basic_search';
    const showPageSize = operation === 'retrieve_files';
    const showQuery = operation !== 'retrieve_files';
    const showTopK = operation !== 'retrieve_files';
    const showTopN = operation === 'search_with_files'
        || (operation === 'basic_search' && useRerankOn);
    const topNRequired = operation === 'search_with_files';

    function handleOperationChange(nextOp) {
        if (nextOp === 'retrieve_files') {
            patch({
                operation: nextOp,
                inputs: {
                    collection_id: inputs.collection_id || '',
                    file_keys: inputs.file_keys || '',
                    page_size: inputs.page_size || ''
                }
            });
            return;
        }
        if (nextOp === 'search_with_files') {
            patch({
                operation: nextOp,
                inputs: {
                    collection_id: inputs.collection_id || '',
                    query: inputs.query || '',
                    top_k: inputs.top_k || '10',
                    top_n: inputs.top_n || '3',
                    use_rerank: inputs.use_rerank ?? 'false',
                    file_keys: inputs.file_keys || ''
                }
            });
            return;
        }
        patch({
            operation: nextOp,
            inputs: {
                collection_id: inputs.collection_id || '',
                query: inputs.query || '',
                top_k: inputs.top_k || '10',
                top_n: inputs.top_n || '3',
                use_rerank: inputs.use_rerank ?? 'false'
            }
        });
    }

    const stepIdOptions = useProcedureStepIds(oelContext);
    const inputClass = cn('h-8 text-[12.5px]', llmFocusInputClass);

    return (
        <LlmStepCanvas>
            <LlmStepZone
                variant="configuration"
                title="Retrieval"
                description="Pick how to reach the knowledge base and which collection to use."
            >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-start">
                    <div className="space-y-3">
                        <LlmLabeled
                            label="Operation mode"
                            help="basic_search: vector similarity. search_with_files: scoped to files. retrieve_files: full files, no semantic search."
                        >
                            <Select value={operation} onValueChange={handleOperationChange}>
                                <SelectTrigger className={inputClass} aria-label="RAG operation">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {OPERATIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>
                                            <span className="font-mono text-[12px]">{o.label}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-[10.5px] text-muted-foreground">
                                {OPERATIONS.find((o) => o.value === operation)?.desc}
                            </p>
                        </LlmLabeled>
                        {showFileKeys && (
                            <LlmLabeled
                                label="File keys (OEL)"
                                help="OEL expression resolving to file keys in the collection — limits search or retrieval to those documents only."
                            >
                                <StepOelEditor
                                    value={inputs.file_keys || ''}
                                    onChange={(v) => patchInputs({ file_keys: v })}
                                    ctx={oelContext}
                                    placeholder="@input.file_keys"
                                    ariaLabel="RAG file keys expression"
                                />
                            </LlmLabeled>
                        )}
                        {showPageSize && (
                            <LlmLabeled
                                label="Page size"
                                help="When paging through file content in retrieve_files mode, how many documents or pages per batch."
                            >
                                <Input
                                    type="number"
                                    value={inputs.page_size ?? ''}
                                    onChange={(e) => patchInputs({
                                        page_size: e.target.value === '' ? undefined : e.target.value
                                    })}
                                    placeholder="20"
                                    className={inputClass}
                                />
                            </LlmLabeled>
                        )}
                    </div>
                    <LlmLabeled
                        label="Collection"
                        help="KB collection to search — create collections in Library → Collections."
                    >
                        <ResourcePicker
                            kind="collection"
                            value={bareCollectionId}
                            onChange={(id) => patchInputs({
                                // ResourcePicker returns the bare id; collection_id is an
                                // OEL expression, so a literal id must be single-quoted
                                // ('id') or the RAG step tries to eval it as an expression
                                // and fails with SYNTAX_ERROR. Empty selection clears it.
                                collection_id: id ? `'${id}'` : ''
                            })}
                            placeholder="No collection selected"
                        />
                    </LlmLabeled>
                </div>
            </LlmStepZone>

            {(showQuery || showTopK || showTopN || showRerank) && (
                <LlmStepZone
                    variant="search"
                    title="Search"
                    description="Query text and limits for semantic retrieval."
                >
                    {showQuery && (
                        <LlmLabeled
                            label="Query (OEL)"
                            help="Text or expression used to find relevant passages — typically the user's question (@input.query) or assembled context from prior steps."
                        >
                            <StepOelEditor
                                value={inputs.query || ''}
                                onChange={(v) => patchInputs({ query: v })}
                                ctx={oelContext}
                                placeholder="@input.message"
                                ariaLabel="RAG query expression"
                            />
                        </LlmLabeled>
                    )}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {showTopK && (
                            <LlmLabeled
                                label="Top k"
                                help="How many candidate passages to retrieve from the vector index before optional reranking. Typical values: 5–20."
                            >
                                <Input
                                    type="number"
                                    value={inputs.top_k ?? ''}
                                    onChange={(e) => patchInputs({
                                        top_k: e.target.value === '' ? undefined : e.target.value
                                    })}
                                    placeholder="10"
                                    className={inputClass}
                                />
                            </LlmLabeled>
                        )}
                        {showTopN && (
                            <LlmLabeled
                                label={topNRequired ? 'Top n *' : 'Top n'}
                                help={topNRequired
                                    ? 'Required for search_with_files — how many passages to return after retrieval (and optional reranking). Typical value: 3.'
                                    : 'After reranking, how many passages to keep in the final result. Only applies when use_rerank is on.'}
                            >
                                <Input
                                    type="number"
                                    value={inputs.top_n ?? ''}
                                    onChange={(e) => patchInputs({
                                        top_n: e.target.value === '' ? undefined : e.target.value
                                    })}
                                    placeholder="3"
                                    className={inputClass}
                                />
                            </LlmLabeled>
                        )}
                        {showRerank && (
                            <LlmLabeled
                                label="Use rerank"
                                help="Re-score the top_k hits with a reranker model for better relevance before returning results."
                            >
                                <div className={cn(
                                    'flex items-center gap-2 rounded-md border px-3 py-2',
                                    'border-sky-500/15 bg-white',
                                    useRerankOn && 'border-sky-500/30 bg-sky-500/[0.06]'
                                )}>
                                    <Switch
                                        checked={useRerankOn}
                                        onCheckedChange={(v) => patchInputs({
                                            use_rerank: String(v),
                                            ...(v || operation === 'search_with_files'
                                                ? {}
                                                : { top_n: undefined })
                                        })}
                                    />
                                    <span className={cn(
                                        'text-sm',
                                        useRerankOn ? 'font-medium text-sky-900/85' : 'text-muted-foreground'
                                    )}>
                                        {useRerankOn ? 'Reranking on' : 'Reranking off'}
                                    </span>
                                </div>
                            </LlmLabeled>
                        )}
                    </div>
                </LlmStepZone>
            )}

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />

            <StepCommonAdvanced
                step={step}
                onPatchStep={onPatchStep}
                oelContext={oelContext}
            />
        </LlmStepCanvas>
    );
}
